import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";

import type { FeatureData } from "./TestedFeaturesSection";
import type { Feature } from "api/memberPortal/features/features/features.types";
import type { AppFormType } from "components/Forms/AppForm";

interface TestedFeaturesFormListProps {
  form: AppFormType | { state: { values: any } };
  fieldName: string;
  handleChange: (_features: FeatureData[]) => void;
  allFeatures: Feature[];
}

const TestedFeaturesFormList = ({
  form,
  fieldName,
  handleChange,
  allFeatures,
}: TestedFeaturesFormListProps) => {
  const [featureInput, setFeatureInput] = useState("");

  const currentFeatures: FeatureData[] = form.state.values[fieldName] || [];

  // Filter out already added features from autocomplete options
  const existingFeatureNames = currentFeatures.map((f) =>
    f.feature_name.toLowerCase(),
  );
  const availableFeatures = allFeatures.filter(
    (f) => !existingFeatureNames.includes(f.name.toLowerCase()),
  );

  const handleAddFeature = (featureName: string) => {
    if (!featureName.trim()) return;

    // Check if feature already exists (case-insensitive)
    const normalizedInput = featureName.trim().toLowerCase();
    const isDuplicate = currentFeatures.some(
      (f) => f.feature_name.toLowerCase() === normalizedInput,
    );

    if (isDuplicate) {
      // Don't add duplicate features
      return;
    }

    // Find if this is an existing feature from the database
    const existingFeature = allFeatures.find(
      (f) => f.name.toLowerCase() === normalizedInput,
    );
    const featureNameToUse = existingFeature
      ? existingFeature.name
      : featureName.trim();

    const newFeature: FeatureData = {
      id: undefined, // New ProductFeature (will be created on submit)
      feature: existingFeature?.id, // Link to Feature table if exists
      feature_name: featureNameToUse,
    };

    handleChange([...currentFeatures, newFeature]);
    setFeatureInput("");
  };

  const handleRemoveFeature = (indexToRemove: number) => {
    handleChange(currentFeatures.filter((_, index) => index !== indexToRemove));
  };

  const handleInputChange = (value: string, reason: string) => {
    // Only update on input changes, not on selection/clear
    if (reason === "input") {
      setFeatureInput(value);
    }
  };

  const handleSelectionChange = (value: string | Feature | null) => {
    if (!value) return;

    const featureName = typeof value === "string" ? value : value.name;
    handleAddFeature(featureName);
  };

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: "bold" }}>
        Tested Features
      </Typography>

      {/* Add Feature Input */}
      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
        <Autocomplete
          freeSolo
          fullWidth
          options={availableFeatures}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name
          }
          inputValue={featureInput}
          onInputChange={(_, value, reason) => handleInputChange(value, reason)}
          onChange={(_, value) => handleSelectionChange(value)}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder="Add feature..."
              size="small"
              onKeyDown={(e) => {
                if (e.key === "Enter" && featureInput.trim()) {
                  e.preventDefault();
                  handleAddFeature(featureInput);
                }
              }}
            />
          )}
        />
        <IconButton
          color="primary"
          onClick={() => handleAddFeature(featureInput)}
          disabled={!featureInput.trim()}
          sx={{ flexShrink: 0 }}
        >
          <AddIcon />
        </IconButton>
      </Box>

      {/* Features List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {currentFeatures.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No tested features added yet
          </Typography>
        ) : (
          currentFeatures.map((feature, index) => (
            <Chip
              key={`feature-${feature.id ?? index}-${feature.feature_name}`}
              label={feature.feature_name}
              onDelete={() => handleRemoveFeature(index)}
              deleteIcon={<DeleteIcon />}
              color="success"
              variant="outlined"
              sx={{
                justifyContent: "space-between",
                height: "auto",
                "& .MuiChip-label": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                },
              }}
            />
          ))
        )}
      </Box>
    </Paper>
  );
};

export default TestedFeaturesFormList;
